import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import QuoteRepository, { QuoteInfo } from "../../../Repositories/QuoteRepository";
import { getActiveBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";
import { isVerifiedTrainer } from "../../../Utils/VerifiedTrainerAmounts";
import { pictureCongrat } from "../../../Utils/Images";
import { DV2 } from "../theme";
import { euros, plural } from "../format";
import { vocab } from "../vocabulary";
import { useDeclarationV2 } from "../DeclarationV2Context";
import { Button, Card, TextLink, useDv2Theme } from "../layout/Ui";

/**
 * Dernier écran : même message et même illustration que l'ancien FinishStep
 * (« Félicitations, déclaration validée ! »), le devis généré avec son
 * téléchargement, puis trois actions. Le client est prévenu par e-mail et
 * dispose de 48 h, ce rappel est donné à l'étape du récapitulatif.
 */
const SuccessStep = () => {
    const { state, reset } = useDeclarationV2();
    const { primary, fontFamily } = useDv2Theme();
    const navigate = useNavigate();
    const courseId = state.declaredCourseId;
    const [quote, setQuote] = useState<QuoteInfo | null>(null);
    const [loading, setLoading] = useState(!!courseId);
    const multiple = state.courseType === "multiple";
    const subject = isVerifiedTrainer() ? null : state.pricing.subject?.fr_FR;
    const prestation = getActivePrestationBrandConfig();

    useEffect(() => {
        if (!courseId) return undefined;
        let cancelled = false;
        // le devis peut mettre quelques instants à être émis : deux tentatives
        const load = (attempt: number) => {
            QuoteRepository.getQuoteForCourse(courseId)
                .then((response) => {
                    if (cancelled) return;
                    if (response?.quote) {
                        setQuote(response.quote);
                        setLoading(false);
                    } else if (attempt < 2) {
                        setTimeout(() => load(attempt + 1), 2500);
                    } else {
                        setLoading(false);
                    }
                })
                .catch(() => { if (!cancelled) setLoading(false); });
        };
        load(0);
        return () => { cancelled = true; };
    }, [courseId]);

    const download = () => {
        if (!quote) return;
        QuoteRepository.download(quote.id)
            .then((response) => { if (response?.url) window.open(response.url, "_blank", "noopener"); })
            .catch(() => undefined);
    };

    const coursesPath = `/${getActiveBrandConfig()}/tableau-de-bord/${prestation}`;
    const goToCourses = () => { reset(); navigate(coursesPath, { state: 9 }); };
    const declareAnother = () => { reset(); };
    const goToDashboard = () => { reset(); navigate(`/${getActiveBrandConfig()}/tableau-de-bord`); };

    // « Cours déclarés ! », « Prestation déclarée ! »
    const titleWord = multiple ? vocab.courses() : vocab.course();
    const title = `${titleWord.charAt(0).toUpperCase()}${titleWord.slice(1)} ${vocab.declared()}${multiple ? "s" : ""} !`;
        // multiple
        // ? `Félicitations, déclaration de plusieurs ${vocab.courses()} validée !`
        // : `Félicitations, déclaration ${vocab.aCourse().replace(/^un /, "d'un ").replace(/^une /, "d'une ")} validée !`;

    return (
        <div style={{ fontFamily, color: DV2.text }} className="w-full">
            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-8 lg:py-12 flex flex-col gap-8">
                <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: DV2.text, lineHeight: 1.2 }}>{title} 🥳</h1>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div className="hidden md:block">
                        <img src={pictureCongrat} alt="" style={{ width: "100%", maxWidth: 440, margin: "0 auto", display: "block" }} />
                    </div>
                    <div className="flex flex-col gap-4" style={{ fontSize: 16, lineHeight: 1.6 }}>
                        <p style={{ margin: 0 }}>
                            Votre {prestation} a été validé avec succès. Il est désormais disponible dans votre tableau de bord, prêt à être consulté et géré.
                            Vous pouvez dès à présent y accéder pour vérifier les détails ou effectuer toute autre action nécessaire.
                            Merci de votre confiance et {getActivePrestationBrandConfig("", "bon ", "bonne ")} !
                        </p>
                        <p style={{ margin: 0, fontStyle: "italic" }}>
                            Votre devis a été généré automatiquement. Il est disponible dans vos documents et dans l'espace de {vocab.yourClient()}.
                        </p>
                    <Card style={{ width: "100%", display: "flex", alignItems: "center", gap: 16, padding: "20px 22px" }} aria-live="polite">
                        <div aria-hidden style={{ width: 48, height: 56, borderRadius: 8, background: DV2.tint, border: "1px solid #C9D9EC", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800, color: primary, flexShrink: 0 }}>PDF</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {loading ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: DV2.muted }}><CircularProgress size={16} /> Génération du devis…</div>
                            ) : quote ? (
                                <>
                                    <div style={{ fontWeight: 800, fontSize: 15 }}>Devis {quote.quoteNumber}</div>
                                    <div style={{ fontSize: 13, color: DV2.muted }}>
                                        {multiple ? plural(state.sessions.length, "séance") : vocab.singleCourseTitle()}{subject ? ` de ${subject.toLowerCase()}` : ""}, {euros(quote.totalAmount)} TTC
                                    </div>
                                </>
                            ) : (
                                <div style={{ fontSize: 14, color: DV2.muted }}>Le devis sera disponible dans quelques instants dans Mes documents et dans l'espace de {vocab.yourClient()}.</div>
                            )}
                        </div>
                        {quote && <Button label="Voir le devis" variant="secondary" size="sm" onClick={download} />}
                    </Card>
                    </div>
                </div>


                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                    <Button label={`Voir ${vocab.theCourse()}`} onClick={goToCourses} className="w-full sm:w-auto" />
                    <Button label={`Déclarer ${vocab.aCourse()} de plus`} variant="secondary" onClick={declareAnother} className="w-full sm:w-auto" />
                    <TextLink label="Retour au tableau de bord" onClick={goToDashboard} size={15} />
                </div>
            </div>
        </div>
    );
};

export default SuccessStep;
